// ============================================================================
// SYSTELOS MONITOR - Sidebar Component
// ============================================================================
// Sidebar 270px, logo usando imagem S.png
// ADAPTADO PARA: MONITOR Standalone
// Versão: 1.0 | Criado: 10/01/2026
// ============================================================================

import { ReactElement } from 'react'
import { useLocation, Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

interface MenuItemBase {
  tipo?: 'header'
  label: string
  restrito?: boolean
}

interface MenuItemLink extends MenuItemBase {
  tipo?: never
  icon: string
  href: string
}

interface MenuItemHeader extends MenuItemBase {
  tipo: 'header'
}

type MenuItem = MenuItemLink | MenuItemHeader

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation()
  const { usuario, logout } = useAuth()

  const niveisAdmin = ['admin']
  const isAdmin = usuario?.nivel ? niveisAdmin.includes(usuario.nivel) : false

  // ============================================
  // MENU DO MONITOR STANDALONE
  // ============================================
  const menuItens: MenuItem[] = [
    { tipo: 'header', label: 'PRINCIPAL' },
    { label: 'Dashboard', icon: 'home', href: '/' },

    { tipo: 'header', label: 'CADASTROS' },
    { label: 'Clientes', icon: 'users', href: '/clientes' },

    { tipo: 'header', label: 'MONITORAMENTO' },
    { label: 'Comunicações', icon: 'file-text', href: '/comunicacoes' },

    { tipo: 'header', label: 'CONFIGURAÇÕES', restrito: true },
    { label: 'Configurações', icon: 'settings', href: '/configuracoes', restrito: true },
  ]

  const menuFiltrado = menuItens.filter(item => {
    if (!item.restrito) return true
    return isAdmin
  })

  const getIniciais = (nome: string): string => {
    if (!nome) return 'U'
    const partes = nome.split(' ')
    if (partes.length >= 2) {
      return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase()
    }
    return nome.substring(0, 2).toUpperCase()
  }

  // Níveis do MONITOR Standalone
  const nivelExibicao: Record<string, string> = {
    admin: 'ADMINISTRADOR',
    operador: 'OPERADOR',
    visualizador: 'VISUALIZADOR',
  }

  const getIcon = (name: string) => {
    const icons: Record<string, ReactElement> = {
      home: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      users: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      'file-text': (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      history: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      settings: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      // Ícone de balança (Scale) - representa o jurídico
      scale: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
        </svg>
      ),
      // Ícone de sino (Bell) - notificações
      bell: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ),
    }

    return icons[name] || icons.home
  }

  const handleLogout = () => {
    if (confirm('Deseja realmente sair do sistema?')) {
      logout()
    }
  }

  return (
    <>
      {/* Overlay escuro */}
      <div
        className={`fixed inset-0 bg-black/50 z-[900] transition-opacity duration-300 ${
          isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        onClick={onClose}
      />

      {/* Sidebar */}
      <nav
        className={`fixed top-0 left-0 h-full w-[270px] z-[1000] flex flex-col transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ backgroundColor: '#0F1B3D' }}
      >
        {/* ====== HEADER DO SIDEBAR ====== */}
        <div
          className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}
        >
          {/* Logo - IMAGEM */}
          <div className="flex items-center gap-3">
            <img
              src="/S.png"
              alt="SYSTELOS MONITOR"
              className="w-9 h-9 rounded-lg"
            />
            <span
              className="text-white text-base font-bold tracking-wide"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              MONITOR
            </span>
          </div>

          {/* Botão fechar */}
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ====== MENU DE NAVEGAÇÃO ====== */}
        <div className="flex-1 overflow-y-auto py-4 px-3">
          {menuFiltrado.map((item, index) => {
            if (item.tipo === 'header') {
              return (
                <div
                  key={index}
                  className="px-3 py-2 mt-4 first:mt-0 text-xs font-semibold tracking-wider"
                  style={{ color: '#9CA3AF' }}
                >
                  {item.label}
                </div>
              )
            }

            const menuItem = item as MenuItemLink
            const isActive = location.pathname === menuItem.href

            return (
              <Link
                key={index}
                to={menuItem.href}
                onClick={onClose}
                className="flex items-center gap-3 px-4 py-3 my-1 rounded-xl transition-all duration-200"
                style={isActive ? {
                  backgroundColor: 'rgba(255,184,0,0.15)',
                  borderLeft: '3px solid #FFB800',
                  color: '#FFFFFF'
                } : {
                  color: '#E5E7EB'
                }}
                onMouseOver={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'
                    e.currentTarget.style.color = '#FFFFFF'
                  }
                }}
                onMouseOut={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'transparent'
                    e.currentTarget.style.color = '#E5E7EB'
                  }
                }}
              >
                <span style={{ color: isActive ? '#FFB800' : 'inherit' }}>
                  {getIcon(menuItem.icon)}
                </span>
                <span className="font-medium text-sm">{menuItem.label}</span>
              </Link>
            )
          })}
        </div>

        {/* ====== FOOTER DO SIDEBAR (Usuário) ====== */}
        <div
          className="p-4"
          style={{
            borderTop: '1px solid rgba(255,255,255,0.1)',
            backgroundColor: 'rgba(0,0,0,0.2)'
          }}
        >
          <div className="flex items-center gap-3 mb-4">
            {usuario?.avatar_url ? (
              <img
                src={usuario.avatar_url}
                alt={usuario.nome}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                style={{ backgroundColor: '#FFB800', color: '#0F1B3D' }}
              >
                {getIniciais(usuario?.nome || '')}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-white font-medium text-sm truncate">
                {usuario?.nome || 'Usuário'}
              </div>
              <div
                className="text-xs font-semibold"
                style={{ color: '#14B8A6' }}
              >
                {nivelExibicao[usuario?.nivel || ''] || usuario?.nivel?.toUpperCase()}
              </div>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all"
            style={{
              backgroundColor: 'rgba(255,184,0,0.1)',
              color: '#FFB800',
              border: '1px solid rgba(255,184,0,0.3)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,184,0,0.2)'
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,184,0,0.1)'
            }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span>Sair do Sistema</span>
          </button>
        </div>
      </nav>
    </>
  )
}

export default Sidebar

